import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Anchor, AlertTriangle, AlertOctagon, Loader2, Plus, Edit2, Trash2, X,
  Sparkles, Layers, ShieldOff, Activity, Cpu, Users, FileText, Zap, ChevronRight,
} from "lucide-react";
import { notify, fmtError } from "@/lib/notify";
import { useTranslation } from "react-i18next";

const COG_TYPES = [
  { id: "capacite", label: "Capacité", icon: Activity },
  { id: "ressource", label: "Ressource", icon: Users },
  { id: "processus", label: "Processus", icon: FileText },
  { id: "actif_strategique", label: "Actif stratégique", icon: Cpu },
];

const CRITICITES = [
  { id: "faible", label: "Faible", color: "#71717a" },
  { id: "moyenne", label: "Moyenne", color: "#378ADD" },
  { id: "haute", label: "Haute", color: "#EF9F27" },
  { id: "critique", label: "Critique", color: "#E24B4A" },
];

const POSITIONS = [
  { id: "core", label: "Cœur de métier" },
  { id: "support", label: "Support" },
  { id: "differenciant", label: "Différenciant" },
];

const PERTURBATIONS = [
  { id: "indisponibilite", label: "Indisponibilité" },
  { id: "degradation", label: "Dégradation" },
  { id: "pic_charge", label: "Pic de charge" },
  { id: "regulation", label: "Réglementaire" },
  { id: "perte_personne", label: "Perte personne-clé" },
  { id: "cyber", label: "Cyber" },
];

const HORIZONS = [
  { id: "court", label: "Court terme" },
  { id: "moyen", label: "Moyen terme" },
  { id: "long", label: "Long terme" },
];

function critColor(c) { return CRITICITES.find((x) => x.id === c)?.color || "#71717a"; }
function critLabelKey(c) { return c || "moyenne"; }
function typeMeta(t) { return COG_TYPES.find((x) => x.id === t) || COG_TYPES[0]; }
function pertLabel(p) { return PERTURBATIONS.find((x) => x.id === p)?.label || p; }
function horizonLabel(h) { return HORIZONS.find((x) => x.id === h)?.label || h; }
function positionLabel(p) { return POSITIONS.find((x) => x.id === p)?.label || p; }


function riskColor(p, i) {
  const s = (p || 0) * (i || 0);
  if (s >= 20) return "#E24B4A";
  if (s >= 12) return "#EF9F27";
  if (s >= 6) return "#378ADD";
  return "#71717a";
}

export default function OperationalGravity() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [editorCenter, setEditorCenter] = useState(null);    // {item}|null
  const [editorScenario, setEditorScenario] = useState(null); // {item, cog_id}|null
  const [predictionFor, setPredictionFor] = useState(null);   // scenario obj
  const [predicting, setPredicting] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/workspace/operational-gravity");
      setData(r.data);
    } catch (e) { setError(fmtError(e)); setData({ centers: [], scenarios: [], metrics: {} }); }
  };
  useEffect(() => { load(); }, []);

  const isEmpty = data && data.centers.length === 0;
  const scenariosByCog = useMemo(() => {
    const map = {};
    (data?.scenarios || []).forEach((s) => {
      (map[s.cog_id] = map[s.cog_id] || []).push(s);
    });
    return map;
  }, [data]);

  const seedDemo = async () => {
    try {
      await api.post("/workspace/operational-gravity/seed-demo");
      notify.success(t("saas.operational_gravity.toast_seeded"));
      await load();
    } catch (e) { notify.error(fmtError(e)); }
  };

  const deleteCenter = async (c) => {
    if (!window.confirm(t("saas.operational_gravity.confirm_delete_center"))) return;
    try {
      await api.delete(`/workspace/operational-gravity/centers/${c.id}`);
      notify.success(t("saas.operational_gravity.toast_center_deleted"));
      await load();
    } catch (e) { notify.error(fmtError(e)); }
  };

  const deleteScenario = async (s) => {
    if (!window.confirm(t("saas.operational_gravity.confirm_delete_scenario"))) return;
    try {
      await api.delete(`/workspace/operational-gravity/scenarios/${s.id}`);
      notify.success(t("saas.operational_gravity.toast_scenario_deleted"));
      await load();
    } catch (e) { notify.error(fmtError(e)); }
  };

  const predict = async (scen) => {
    setPredicting(true);
    setPredictionFor(scen);
    try {
      const r = await api.post(`/workspace/operational-gravity/scenarios/${scen.id}/predict`);
      setPredictionFor({ ...scen, claude_prediction: r.data });
      notify.success(t("saas.operational_gravity.toast_predicted"));
      await load();
    } catch (e) {
      notify.error(fmtError(e));
      setPredictionFor(null);
    } finally { setPredicting(false); }
  };

  const analyzePortfolio = async () => {
    setAnalyzing(true);
    try {
      const r = await api.post("/workspace/operational-gravity/analyze");
      setAnalysis(r.data);
      notify.success(t("saas.operational_gravity.toast_analyzed"));
    } catch (e) { notify.error(fmtError(e)); }
    finally { setAnalyzing(false); }
  };

  return (
    <div className="space-y-4" data-testid="og-root">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-[#E24B4A] noam-overline">
            <Anchor className="w-3 h-3" /> {t("saas.operational_gravity.overline")}
          </div>
          <h1 className="noam-display text-2xl font-bold tracking-tight mt-1">{t("saas.operational_gravity.title")}</h1>
          <p className="text-zinc-400 text-xs mt-1 max-w-3xl">
            {t("saas.operational_gravity.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={analyzePortfolio} disabled={analyzing || isEmpty}
            className="btn-ghost text-xs disabled:opacity-50" data-testid="og-analyze-btn">
            {analyzing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("saas.operational_gravity.analyzing")}</> :
              <><Sparkles className="w-3.5 h-3.5" /> {t("saas.operational_gravity.analyze_btn")}</>}
          </button>
          <button onClick={() => setEditorCenter({ item: null })}
            className="btn-primary text-xs" data-testid="og-create-center-btn">
            <Plus className="w-3.5 h-3.5" /> {t("saas.operational_gravity.add_center_btn")}
          </button>
        </div>
      </header>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /><span>{error}</span>
          <button className="ml-auto" onClick={() => setError(null)}><X className="w-3 h-3" /></button>
        </div>
      )}

      {/* KPIs */}
      {data && !isEmpty && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="og-kpis">
          <KpiCard label={t("saas.operational_gravity.kpi_centers")} value={data.metrics.total_centers} icon={Layers} color="#7F77DD" />
          <KpiCard label={t("saas.operational_gravity.kpi_scenarios")} value={data.metrics.total_scenarios} icon={Activity} color="#378ADD" />
          <KpiCard label={t("saas.operational_gravity.kpi_robustness")} value={`${data.metrics.avg_robustesse}/100`}
            icon={Zap} color={data.metrics.avg_robustesse >= 60 ? "#1D9E75" : data.metrics.avg_robustesse >= 40 ? "#EF9F27" : "#E24B4A"} />
          <KpiCard label={t("saas.operational_gravity.kpi_fragile")} value={data.metrics.fragile_centers?.length || 0}
            icon={ShieldOff} color={data.metrics.fragile_centers?.length ? "#E24B4A" : "#71717a"} />
        </div>
      )}

      {analysis && <AnalysisCard analysis={analysis} centers={data.centers} onClose={() => setAnalysis(null)} />}

      {isEmpty ? (
        <div className="noam-card p-10 text-center" data-testid="og-empty">
          <div className="w-14 h-14 mx-auto rounded-xl bg-[#E24B4A]/10 border border-[#E24B4A]/30 flex items-center justify-center">
            <Anchor className="w-6 h-6 text-[#E24B4A]" />
          </div>
          <h2 className="noam-display text-xl font-semibold mt-4">{t("saas.operational_gravity.empty_title")}</h2>
          <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">
            {t("saas.operational_gravity.empty_body")}
          </p>
          <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
            <button onClick={seedDemo} className="btn-primary text-sm" data-testid="og-seed-btn">
              <Sparkles className="w-4 h-4" /> {t("saas.operational_gravity.seed_btn")}
            </button>
            <button onClick={() => setEditorCenter({ item: null })} className="btn-ghost text-sm">
              <Plus className="w-4 h-4" /> {t("saas.operational_gravity.create_first")}
            </button>
          </div>
        </div>
      ) : data && (
        <div className="grid grid-cols-12 gap-3" data-testid="og-content">
          {/* Centers column */}
          <div className="col-span-12 lg:col-span-7 space-y-3">
            <div className="flex items-center justify-between">
              <div className="noam-overline text-zinc-500">{t("saas.operational_gravity.kpi_centers")} ({data.centers.length})</div>
            </div>
            {data.centers.map((c) => (
              <CenterCard key={c.id} center={c}
                fragile={(data.metrics.fragile_centers || []).includes(c.id)}
                onEdit={() => setEditorCenter({ item: c })}
                onDelete={() => deleteCenter(c)}
                onAddScenario={() => setEditorScenario({ item: null, cog_id: c.id })}
                scenarios={scenariosByCog[c.id] || []}
                onScenarioClick={(s) => setPredictionFor(s)}
                onScenarioEdit={(s) => setEditorScenario({ item: s, cog_id: c.id })}
                onScenarioDelete={(s) => deleteScenario(s)}
              />
            ))}
          </div>
          {/* Right column: risk matrix + legend */}
          <aside className="col-span-12 lg:col-span-5 space-y-3">
            <RiskMatrix scenarios={data.scenarios} centers={data.centers}
              onScenarioClick={(s) => setPredictionFor(s)} />
            <KeyQuestionsCard />
          </aside>
        </div>
      )}

      {editorCenter && (
        <CenterEditor item={editorCenter.item}
          onClose={() => setEditorCenter(null)}
          onSaved={async () => { setEditorCenter(null); await load(); }} />
      )}
      {editorScenario && (
        <ScenarioEditor item={editorScenario.item} cogId={editorScenario.cog_id}
          centers={data?.centers || []}
          onClose={() => setEditorScenario(null)}
          onSaved={async () => { setEditorScenario(null); await load(); }} />
      )}
      {predictionFor && (
        <PredictionModal scenario={predictionFor}
          center={(data?.centers || []).find((c) => c.id === predictionFor.cog_id)}
          predicting={predicting}
          onPredict={() => predict(predictionFor)}
          onClose={() => setPredictionFor(null)} />
      )}
    </div>
  );
}

// ---------------- KPI ----------------
function KpiCard({ label, value, icon: Icon, color }) {
  return (
    <div className="noam-card p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
        <div className="w-7 h-7 rounded flex items-center justify-center"
          style={{ background: `${color}1A`, border: `1px solid ${color}40` }}>
          <Icon className="w-3.5 h-3.5" style={{ color }} />
        </div>
      </div>
      <div className="text-xl font-bold text-white mt-1.5">{value}</div>
    </div>
  );
}

// ---------------- Center Card ----------------
function CenterCard({ center, fragile, scenarios, onEdit, onDelete, onAddScenario,
  onScenarioClick, onScenarioEdit, onScenarioDelete }) {
  const { t } = useTranslation();
  const col = critColor(center.criticite);
  const T = typeMeta(center.type);
  const TIcon = T.icon;
  const robCol = center.robustesse >= 60 ? "#1D9E75" : center.robustesse >= 40 ? "#EF9F27" : "#E24B4A";
  return (
    <div className="noam-card p-4" data-testid={`og-center-${center.id}`}
      style={{ borderColor: fragile ? `${col}60` : undefined }}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
            style={{ background: `${col}15`, border: `1px solid ${col}40` }}>
            <TIcon className="w-4 h-4" style={{ color: col }} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white truncate">{center.name}</div>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              <span className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider"
                style={{ background: `${col}1A`, color: col, border: `1px solid ${col}40` }}>
                {t(`saas.operational_gravity.criticite.${critLabelKey(center.criticite)}`, { defaultValue: center.criticite })}
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-zinc-300 border border-white/10">
                {t(`saas.operational_gravity.type.${center.type}`, { defaultValue: T.label })}
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-zinc-300 border border-white/10">
                {t(`saas.operational_gravity.position.${center.position_strategique}`, { defaultValue: positionLabel(center.position_strategique) })}
              </span>
              {fragile && (
                <span className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider bg-[#E24B4A]/15 text-[#E24B4A] border border-[#E24B4A]/40 inline-flex items-center gap-0.5">
                  <ShieldOff className="w-2.5 h-2.5" /> {t("saas.operational_gravity.fragile_badge")}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={onEdit} className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-white"
            data-testid={`og-center-edit-${center.id}`} title="Modifier">
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button onClick={onDelete} className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-[#E24B4A]"
            data-testid={`og-center-delete-${center.id}`} title="Supprimer">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {center.description && <p className="text-xs text-zinc-300 leading-relaxed mb-3">{center.description}</p>}

      {/* Robustness bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
          <span>{t("saas.operational_gravity.robustness")}</span><span className="font-mono text-zinc-300">{center.robustesse}/100</span>
        </div>
        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${center.robustesse}%`, background: robCol }} />
        </div>
      </div>

      {/* Vulnerabilities / signals chips */}
      <div className="space-y-1.5">
        {center.vulnerabilites?.length > 0 && (
          <ChipRow label={t("saas.operational_gravity.vulnerabilities")} items={center.vulnerabilites} color="#EF9F27" />
        )}
        {center.signaux_faibles?.length > 0 && (
          <ChipRow label={t("saas.operational_gravity.weak_signals")} items={center.signaux_faibles} color="#7F77DD" />
        )}
        {center.dependances?.length > 0 && (
          <ChipRow label={t("saas.operational_gravity.dependencies")} items={center.dependances} color="#378ADD" />
        )}
      </div>

      {/* Scenarios */}
      <div className="mt-3 pt-3 border-t border-white/5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">
            {t("saas.operational_gravity.scenarios_count")} ({scenarios.length})
          </div>
          <button onClick={onAddScenario}
            className="text-[10px] text-zinc-400 hover:text-white inline-flex items-center gap-1"
            data-testid={`og-add-scenario-${center.id}`}>
            <Plus className="w-3 h-3" /> {t("saas.operational_gravity.add_scenario_btn")}
          </button>
        </div>
        {scenarios.length === 0 ? (
          <div className="text-[11px] text-zinc-600 italic">{t("saas.operational_gravity.no_scenarios")}</div>
        ) : (
          <div className="space-y-1.5">
            {scenarios.map((s) => (
              <ScenarioRow key={s.id} scenario={s}
                onClick={() => onScenarioClick(s)}
                onEdit={() => onScenarioEdit(s)}
                onDelete={() => onScenarioDelete(s)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ChipRow({ label, items, color }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <div className="flex flex-wrap gap-1">
        {items.map((it, i) => (
          <span key={i} className="text-[10px] px-1.5 py-0.5 rounded border"
            style={{ background: `${color}10`, color: color, borderColor: `${color}30` }}>
            {it}
          </span>
        ))}
      </div>
    </div>
  );
}

function ScenarioRow({ scenario, onClick, onEdit, onDelete }) {
  const { t } = useTranslation();
  const col = riskColor(scenario.probabilite, scenario.impact);
  const score = scenario.probabilite * scenario.impact;
  const hasPrediction = !!scenario.claude_prediction;
  return (
    <div className="flex items-center gap-2 p-2 rounded border border-white/5 hover:border-white/10 hover:bg-white/[0.02] transition cursor-pointer group"
      onClick={onClick} data-testid={`og-scenario-${scenario.id}`}>
      <div className="w-1.5 h-8 rounded-full shrink-0" style={{ background: col }} />
      <div className="flex-1 min-w-0">
        <div className="text-xs text-white truncate">{scenario.name}</div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className="text-[9px] text-zinc-500">{t(`saas.operational_gravity.perturbation.${scenario.type_perturbation}`, { defaultValue: pertLabel(scenario.type_perturbation) })}</span>
          <span className="text-[9px] text-zinc-600">·</span>
          <span className="text-[9px] text-zinc-500">{t(`saas.operational_gravity.horizon.${scenario.horizon}`, { defaultValue: horizonLabel(scenario.horizon) })}</span>
          {hasPrediction && (
            <>
              <span className="text-[9px] text-zinc-600">·</span>
              <span className="text-[9px] text-[#7F77DD] inline-flex items-center gap-0.5">
                <Sparkles className="w-2 h-2" /> {t("saas.operational_gravity.claude_synthesis")}
              </span>
            </>
          )}
        </div>
      </div>
      <div className="text-[10px] font-mono shrink-0 px-1.5 py-0.5 rounded"
        style={{ background: `${col}15`, color: col, border: `1px solid ${col}40` }}>
        P{scenario.probabilite}×I{scenario.impact}={score}
      </div>
      <div className="opacity-0 group-hover:opacity-100 transition flex items-center gap-1 shrink-0">
        <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="p-1 rounded hover:bg-white/5 text-zinc-500 hover:text-white">
          <Edit2 className="w-3 h-3" />
        </button>
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-1 rounded hover:bg-white/5 text-zinc-500 hover:text-[#E24B4A]">
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

// ---------------- Risk Matrix (5×5) ----------------
function RiskMatrix({ scenarios, centers, onScenarioClick }) {
  const { t } = useTranslation();
  const cogName = (id) => centers.find((c) => c.id === id)?.name || "?";
  const matrix = Array.from({ length: 5 }, () => Array.from({ length: 5 }, () => []));
  scenarios.forEach((s) => {
    const p = Math.min(5, Math.max(1, s.probabilite || 1)) - 1;
    const i = Math.min(5, Math.max(1, s.impact || 1)) - 1;
    matrix[4 - i][p].push(s); // y reversed: high impact = top
  });
  return (
    <div className="noam-card p-4" data-testid="og-risk-matrix">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="noam-overline">{t("saas.operational_gravity.matrix_title")}</div>
          <div className="text-[11px] text-zinc-500 mt-0.5">{t("saas.operational_gravity.matrix_sub")}</div>
        </div>
      </div>
      <div className="flex">
        {/* Y axis label */}
        <div className="flex flex-col justify-between mr-2 py-0.5 text-[9px] uppercase tracking-wider text-zinc-500">
          <span>5</span><span>4</span><span>3</span><span>2</span><span>1</span>
        </div>
        <div className="flex-1">
          <div className="grid grid-cols-5 gap-0.5">
            {matrix.flat().map((cell, idx) => {
              const row = Math.floor(idx / 5);
              const col = idx % 5;
              const impact = 5 - row;
              const proba = col + 1;
              const score = proba * impact;
              const bg = score >= 20 ? "#E24B4A" : score >= 12 ? "#EF9F27" : score >= 6 ? "#378ADD" : "#3f3f46";
              return (
                <div key={idx} className="aspect-square rounded relative group cursor-default"
                  style={{ background: `${bg}25`, border: `1px solid ${bg}55` }}>
                  {cell.length > 0 && (
                    <div className="absolute inset-0 flex items-center justify-center text-[11px] font-bold" style={{ color: bg }}>
                      {cell.length}
                    </div>
                  )}
                  {cell.length > 0 && (
                    <div className="absolute z-10 left-1/2 -translate-x-1/2 top-full mt-1 hidden group-hover:block bg-[#0a0a0a] border border-white/10 rounded p-2 min-w-[180px] shadow-xl">
                      {cell.map((s) => (
                        <div key={s.id} className="text-[10px] py-0.5 hover:text-white cursor-pointer"
                          onClick={() => onScenarioClick(s)}>
                          <div className="text-zinc-200 truncate">{s.name}</div>
                          <div className="text-zinc-500 text-[9px]">{cogName(s.cog_id)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-1 text-[9px] uppercase tracking-wider text-zinc-500">
            <span>P1</span><span>P2</span><span>P3</span><span>P4</span><span>P5</span>
          </div>
        </div>
      </div>
      <div className="text-[10px] text-zinc-500 italic mt-3">Cliquez sur un scénario survolé pour ouvrir la prédiction NOAM.</div>
    </div>
  );
}

function KeyQuestionsCard() {
  const { t } = useTranslation();
  return (
    <div className="noam-card p-4">
      <div className="noam-overline mb-2">{t("saas.operational_gravity.questions_title")}</div>
      <ul className="space-y-2 text-xs text-zinc-300">
        <li className="flex items-start gap-2">
          <span className="text-[#E24B4A] font-bold shrink-0">{t("saas.operational_gravity.q_what")}</span>
          <span>{t("saas.operational_gravity.q_what_body")}</span>
        </li>
        <li className="flex items-start gap-2">
          <span className="text-[#EF9F27] font-bold shrink-0">{t("saas.operational_gravity.q_break")}</span>
          <span>{t("saas.operational_gravity.q_break_body")}</span>
        </li>
        <li className="flex items-start gap-2">
          <span className="text-[#7F77DD] font-bold shrink-0">{t("saas.operational_gravity.q_time")}</span>
          <span>{t("saas.operational_gravity.q_time_body")}</span>
        </li>
      </ul>
    </div>
  );
}

// ---------------- Editors ----------------
function CenterEditor({ item, onClose, onSaved }) {
  const { t } = useTranslation();
  const isEdit = !!item;
  const [form, setForm] = useState(() => item ? {
    ...item,
    dependances_str: (item.dependances || []).join("\n"),
    vulnerabilites_str: (item.vulnerabilites || []).join("\n"),
    signaux_str: (item.signaux_faibles || []).join("\n"),
  } : {
    name: "", description: "", type: "capacite", criticite: "haute", position_strategique: "core", robustesse: 50,
    dependances_str: "", vulnerabilites_str: "", signaux_str: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const toList = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = {
        name: form.name.trim(),
        description: (form.description || "").trim(),
        type: form.type, criticite: form.criticite, position_strategique: form.position_strategique,
        robustesse: parseInt(form.robustesse, 10) || 0,
        dependances: toList(form.dependances_str),
        vulnerabilites: toList(form.vulnerabilites_str),
        signaux_faibles: toList(form.signaux_str),
      };
      if (isEdit) {
        await api.patch(`/workspace/operational-gravity/centers/${item.id}`, payload);
        notify.success(t("saas.operational_gravity.toast_center_updated"));
      } else {
        await api.post("/workspace/operational-gravity/centers", payload);
        notify.success(t("saas.operational_gravity.toast_center_created"));
      }
      onSaved();
    } catch (e) { setErr(fmtError(e)); }
    finally { setBusy(false); }
  };

  return (
    <ModalShell onClose={onClose} title={isEdit ? t("saas.operational_gravity.modal_edit_center") : t("saas.operational_gravity.modal_new_center")}
      overline={isEdit ? "" : ""} testid="og-center-editor">
      <form onSubmit={submit} className="p-5 space-y-4">
        <Field label="Nom *">
          <input required autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Ex : Plateforme core banking Avaloq" className="noam-input" data-testid="og-center-name" />
        </Field>
        <Field label="Description">
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2} placeholder="Pourquoi ce centre est critique pour l'organisation…" className="noam-input" />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Type">
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="noam-input">
              {COG_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Criticité">
            <select value={form.criticite} onChange={(e) => setForm({ ...form, criticite: e.target.value })} className="noam-input">
              {CRITICITES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Position">
            <select value={form.position_strategique} onChange={(e) => setForm({ ...form, position_strategique: e.target.value })} className="noam-input">
              {POSITIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </Field>
        </div>
        <Field label={`Robustesse · ${form.robustesse}/100`}>
          <input type="range" min={0} max={100} value={form.robustesse}
            onChange={(e) => setForm({ ...form, robustesse: e.target.value })}
            className="w-full" data-testid="og-center-robustesse" />
          <div className="flex justify-between text-[9px] text-zinc-500 mt-0.5">
            <span>Fragile (0)</span><span>Résilient (100)</span>
          </div>
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Dépendances (1 par ligne)">
            <textarea value={form.dependances_str} onChange={(e) => setForm({ ...form, dependances_str: e.target.value })}
              rows={3} placeholder="Vendor Avaloq&#10;Équipe IT&#10;Datacenter" className="noam-input text-xs" />
          </Field>
          <Field label="Vulnérabilités (1 par ligne)">
            <textarea value={form.vulnerabilites_str} onChange={(e) => setForm({ ...form, vulnerabilites_str: e.target.value })}
              rows={3} placeholder="Vendor lock-in&#10;1 seul DBA" className="noam-input text-xs" />
          </Field>
          <Field label="Signaux faibles (1 par ligne)">
            <textarea value={form.signaux_str} onChange={(e) => setForm({ ...form, signaux_str: e.target.value })}
              rows={3} placeholder="Tickets >72h&#10;Latence en hausse" className="noam-input text-xs" />
          </Field>
        </div>
        {err && (
          <div className="flex items-start gap-2 p-2.5 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {err}
          </div>
        )}
        <ModalFooter onClose={onClose} busy={busy} isEdit={isEdit} testid="og-center-submit" />
      </form>
    </ModalShell>
  );
}

function ScenarioEditor({ item, cogId, centers, onClose, onSaved }) {
  const { t } = useTranslation();
  const isEdit = !!item;
  const [form, setForm] = useState(() => item ? {
    ...item, mitigations_str: (item.mitigations_en_place || []).join("\n"),
  } : {
    cog_id: cogId, name: "", description: "", type_perturbation: "indisponibilite",
    probabilite: 3, impact: 3, horizon: "court", mitigations_str: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const toList = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = {
        cog_id: form.cog_id,
        name: form.name.trim(),
        description: (form.description || "").trim(),
        type_perturbation: form.type_perturbation,
        probabilite: parseInt(form.probabilite, 10) || 3,
        impact: parseInt(form.impact, 10) || 3,
        horizon: form.horizon,
        mitigations_en_place: toList(form.mitigations_str),
      };
      if (isEdit) {
        await api.patch(`/workspace/operational-gravity/scenarios/${item.id}`, payload);
        notify.success(t("saas.operational_gravity.toast_scenario_updated"));
      } else {
        await api.post("/workspace/operational-gravity/scenarios", payload);
        notify.success(t("saas.operational_gravity.toast_scenario_created"));
      }
      onSaved();
    } catch (e) { setErr(fmtError(e)); }
    finally { setBusy(false); }
  };

  return (
    <ModalShell onClose={onClose} title={isEdit ? t("saas.operational_gravity.modal_edit_scenario") : t("saas.operational_gravity.modal_new_scenario")}
      overline={""} testid="og-scenario-editor">
      <form onSubmit={submit} className="p-5 space-y-4">
        {!isEdit && centers.length > 1 && (
          <Field label="Centre de gravité ciblé">
            <select value={form.cog_id} onChange={(e) => setForm({ ...form, cog_id: e.target.value })}
              className="noam-input" data-testid="og-scenario-cog">
              {centers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
        )}
        <Field label="Nom du scénario *">
          <input required autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Ex : Indisponibilité Avaloq 72h" className="noam-input" data-testid="og-scenario-name" />
        </Field>
        <Field label="Description">
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2} placeholder="Que se passe-t-il concrètement ?" className="noam-input" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type de perturbation">
            <select value={form.type_perturbation} onChange={(e) => setForm({ ...form, type_perturbation: e.target.value })} className="noam-input">
              {PERTURBATIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </Field>
          <Field label="Horizon">
            <select value={form.horizon} onChange={(e) => setForm({ ...form, horizon: e.target.value })} className="noam-input">
              {HORIZONS.map((h) => <option key={h.id} value={h.id}>{h.label}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={`Probabilité · ${form.probabilite}/5`}>
            <input type="range" min={1} max={5} value={form.probabilite}
              onChange={(e) => setForm({ ...form, probabilite: e.target.value })} className="w-full" />
          </Field>
          <Field label={`Impact · ${form.impact}/5`}>
            <input type="range" min={1} max={5} value={form.impact}
              onChange={(e) => setForm({ ...form, impact: e.target.value })} className="w-full" />
          </Field>
        </div>
        <Field label="Mitigations en place (1 par ligne)">
          <textarea value={form.mitigations_str} onChange={(e) => setForm({ ...form, mitigations_str: e.target.value })}
            rows={3} placeholder="DRP testé 1×/an&#10;Sauvegardes J-1&#10;Procédure papier 48h" className="noam-input text-xs" />
        </Field>
        {err && (
          <div className="flex items-start gap-2 p-2.5 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {err}
          </div>
        )}
        <ModalFooter onClose={onClose} busy={busy} isEdit={isEdit} testid="og-scenario-submit" />
      </form>
    </ModalShell>
  );
}

// ---------------- Prediction Modal ----------------
function PredictionModal({ scenario, center, predicting, onPredict, onClose }) {
  const { t } = useTranslation();
  const p = scenario.claude_prediction;
  return (
    <ModalShell onClose={onClose} title={scenario.name} overline={center ? `${t("saas.operational_gravity.add_scenario_btn")} · ${center.name}` : t("saas.operational_gravity.add_scenario_btn")} testid="og-prediction-modal" wide>
      <div className="p-5 space-y-4">
        <div className="flex items-center gap-2 flex-wrap text-[11px]">
          <Badge color={riskColor(scenario.probabilite, scenario.impact)}>
            P{scenario.probabilite}×I{scenario.impact} = {scenario.probabilite * scenario.impact}
          </Badge>
          <Badge>{t(`saas.operational_gravity.perturbation.${scenario.type_perturbation}`, { defaultValue: pertLabel(scenario.type_perturbation) })}</Badge>
          <Badge>{t(`saas.operational_gravity.horizon.${scenario.horizon}`, { defaultValue: horizonLabel(scenario.horizon) })}</Badge>
        </div>
        {scenario.description && <p className="text-sm text-zinc-300 leading-relaxed">{scenario.description}</p>}
        {scenario.mitigations_en_place?.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">{t("saas.operational_gravity.mitigations_in_place")}</div>
            <ul className="space-y-1">
              {scenario.mitigations_en_place.map((m, i) => (
                <li key={i} className="text-xs text-zinc-300 flex items-start gap-2">
                  <span className="w-1 h-1 mt-1.5 rounded-full bg-[#1D9E75] shrink-0" /> {m}
                </li>
              ))}
            </ul>
          </div>
        )}

        {!p && !predicting && (
          <div className="rounded-md border border-dashed border-white/10 p-6 text-center">
            <Sparkles className="w-6 h-6 text-[#7F77DD] mx-auto" />
            <div className="text-sm text-zinc-300 mt-2">{t("saas.operational_gravity.no_prediction")}</div>
            <button onClick={onPredict} className="btn-primary text-xs mt-3" data-testid="og-predict-btn">
              <Sparkles className="w-3.5 h-3.5" /> {t("saas.operational_gravity.predict_btn")}
            </button>
          </div>
        )}
        {predicting && (
          <div className="rounded-md border border-white/10 p-6 text-center">
            <Loader2 className="w-6 h-6 text-[#7F77DD] mx-auto animate-spin" />
            <div className="text-sm text-zinc-300 mt-2">{t("saas.operational_gravity.predicting")}</div>
          </div>
        )}
        {p && !predicting && (
          <div className="space-y-3" data-testid="og-prediction-content">
            {p.synthese && (
              <div className="p-3 rounded-md bg-[#7F77DD]/10 border border-[#7F77DD]/30">
                <div className="noam-overline text-[#7F77DD] mb-1">{t("saas.operational_gravity.claude_synthesis")}</div>
                <p className="text-sm text-zinc-100 leading-relaxed">{p.synthese}</p>
                {p.severite_predite && (
                  <div className="mt-2 text-[10px] text-zinc-400">
                    {t("saas.operational_gravity.predicted_severity")} : <span className="text-zinc-200 font-mono">{p.severite_predite}/5</span>
                  </div>
                )}
              </div>
            )}
            {p.timeline?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">{t("saas.operational_gravity.trajectory")}</div>
                <div className="space-y-2">
                  {p.timeline.map((tt, i) => (
                    <div key={i} className="flex items-start gap-3 p-2.5 rounded border border-white/5 bg-white/[0.02]">
                      <div className="w-16 shrink-0">
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#EF9F27]/15 text-[#EF9F27] border border-[#EF9F27]/40 font-mono">
                          {tt.horizon}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-zinc-200">{tt.impact}</div>
                        {tt.action && (
                          <div className="text-[11px] text-zinc-400 mt-1 flex items-start gap-1.5">
                            <ChevronRight className="w-3 h-3 mt-0.5 text-[#1D9E75]" /> {tt.action}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-3">
              {p.effets_cascade?.length > 0 && (
                <SectionList title={t("saas.operational_gravity.cascade_effects")} items={p.effets_cascade.map((x) => `${x.domaine ? `[${x.domaine}] ` : ""}${x.description || ""}`)} color="#E24B4A" />
              )}
              {p.signaux_alerte_precoce?.length > 0 && (
                <SectionList title={t("saas.operational_gravity.early_signals")} items={p.signaux_alerte_precoce} color="#EF9F27" />
              )}
            </div>
            {p.mitigations_recommandees?.length > 0 && (
              <SectionList title={t("saas.operational_gravity.recommended_mitigations")} items={p.mitigations_recommandees} color="#1D9E75" />
            )}
            <div className="text-right pt-2 border-t border-white/5">
              <button onClick={onPredict} className="btn-ghost text-[11px]" data-testid="og-predict-rerun-btn">
                <Sparkles className="w-3 h-3" /> {t("saas.operational_gravity.rerun")}
              </button>
            </div>
          </div>
        )}
      </div>
    </ModalShell>
  );
}

function SectionList({ title, items, color }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color }}>{title}</div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-xs text-zinc-300 flex items-start gap-2">
            <span className="w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ background: color }} /> {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

function AnalysisCard({ analysis, centers, onClose }) {
  const { t } = useTranslation();
  const findName = (id) => centers.find((c) => c.id === id)?.name || id;
  return (
    <div className="noam-card p-5" data-testid="og-analysis-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="noam-overline text-[#7F77DD]">{t("saas.operational_gravity.analysis_title")}</div>
          <div className="noam-display text-base font-semibold mt-1">{t("saas.operational_gravity.claude_synthesis")}</div>
        </div>
        <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
      </div>
      {analysis.synthese && <p className="text-sm text-zinc-200 leading-relaxed mb-4">{analysis.synthese}</p>}
      <div className="grid md:grid-cols-2 gap-3">
        {analysis.piliers_fragiles?.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wider mb-1.5 text-[#E24B4A]">{t("saas.operational_gravity.fragile_pillars")}</div>
            <ul className="space-y-2">
              {analysis.piliers_fragiles.map((p, i) => (
                <li key={i} className="text-xs">
                  <div className="text-zinc-200 font-medium">{findName(p.cog_id)}</div>
                  <div className="text-zinc-500 text-[11px] mt-0.5">{p.raison}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
        {analysis.concentrations_risque?.length > 0 && (
          <SectionList title={t("saas.operational_gravity.risk_concentrations")} items={analysis.concentrations_risque.map((x) => x.raison)} color="#EF9F27" />
        )}
        {analysis.redondances_manquantes?.length > 0 && (
          <SectionList title={t("saas.operational_gravity.missing_redundancies")} items={analysis.redondances_manquantes.map((x) => x.raison)} color="#378ADD" />
        )}
        {analysis.signaux_convergents?.length > 0 && (
          <SectionList title={t("saas.operational_gravity.convergent_signals")} items={analysis.signaux_convergents} color="#7F77DD" />
        )}
      </div>
      {analysis.actions_prioritaires?.length > 0 && (
        <div className="mt-4 pt-3 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">{t("saas.systemic.priority_actions")}</div>
          <ul className="space-y-1.5">
            {analysis.actions_prioritaires.map((a, i) => {
              const col = a.priorite === "haute" ? "#E24B4A" : a.priorite === "moyenne" ? "#EF9F27" : "#71717a";
              return (
                <li key={i} className="text-xs flex items-start gap-2">
                  <span className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider shrink-0 mt-0.5"
                    style={{ background: `${col}1A`, color: col, border: `1px solid ${col}40` }}>
                    {a.priorite}
                  </span>
                  <span className="text-zinc-200">{a.action}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------- Shared UI primitives ----------------
function ModalShell({ children, onClose, title, overline, testid, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-start justify-center overflow-y-auto p-4 py-12" onClick={onClose}>
      <div className={`noam-card w-full ${wide ? "max-w-3xl" : "max-w-2xl"}`} onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-start justify-between p-5 border-b border-white/5">
          <div>
            {overline && <div className="noam-overline">{overline}</div>}
            <div className="noam-display text-base font-semibold mt-0.5">{title}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalFooter({ onClose, busy, isEdit, testid }) {
  return (
    <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/5">
      <button type="button" onClick={onClose} className="btn-ghost text-xs">Annuler</button>
      <button type="submit" disabled={busy} className="btn-primary text-xs disabled:opacity-50" data-testid={testid}>
        {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enregistrement…</> : <>{isEdit ? "Enregistrer" : "Créer"}</>}
      </button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">{label}</label>
      {children}
    </div>
  );
}

function Badge({ color, children }) {
  const c = color || "#71717a";
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider font-mono"
      style={{ background: `${c}1A`, color: c, border: `1px solid ${c}40` }}>
      {children}
    </span>
  );
}
